import React, { useState } from 'react';
import requiredIf from 'react-required-if';
import PropTypes from 'prop-types';
import { useFormikContext } from 'formik';

import { useStyles } from '~/assets/styles';
import CommunicationExposureLabels from '~/components/communications/CommunicationCard/CommunicationExposureLabels';
import CommunicationFullHeader from '~/components/communications/CommunicationCard/CommunicationFullHeader';
import CommunicationCategoryChip from '~/components/communications/CommunicationCategoryChip';
import CommunicationCategorySelectChip from '~/components/communications/CommunicationCategorySelectChip';
import CommunicationHandledStatusChip from '~/components/communications/CommunicationHandledStatusChip';
import { useFetchClaim } from '~/hooks/useFetchClaim';
import { displayCommunicationCategories } from '~/Utils';

import CardDialog from '../CardDialog';
import { useClaim } from '../ClaimContainer';
import ClaimLink from '../ClaimLink';
import CliveAnalysisSection from '../Clive/CliveCommunications/CliveAnalysisSection';
import CliveRecommendationPanel from '../Clive/CliveRecommendationPanel/CliveRecommendationPanel';
import { FsIconButton, PERMISSION_VERBS, RestrictedPermissions } from '../core';
import { ErrorHelperTextFormik } from '../core/Formik/ErrorHelperTextFormik';
import { ExpandIcon } from '../icons';
import CloseIcon from '../icons/CloseIcon';
import useOrganization from '../OrganizationContext';
import { useCliveConfigOptions } from '../SystemConfiguration/CliveConfiguration/CliveConfigurationContext';
import { TextFieldFormik } from '../TextFieldFormik';

const CommunicationCard = (props) => {
  const {
    open,
    communication,
    communicationInitialFields,
    communicationHeaderAdditionalProps,
    CommunicationTypeSpecificBody,
    communicationTypeSpecificBodyProps,
    CommunicationAction,
    communicationActionProps,
    communicationIdentifier,
    additionalCardActions,
    disableSoftClose,
    isEditing,
    isNew,
    isDialog,
    isDocument,
    onRequestEdit,
    onClose,
    onMinimized,
    disableMinimized,
    title,
    maxWidth,
    isCommunicationNotRelatedToClaimInContext,
    senderFragment,
    leftCardActions,
    onCommunicationUpdate,
    onRefreshViewPanelData,
    onRefreshCurrentPage,
    cardDialogContainerStyle,
    cardDialogContainerClassName,
    AdditionalDialogHeader = <React.Fragment />,
    showTranslation,
    BannerBelowHeader,
    newPhoneCommunicationUI,
    showClive,
    shouldShowClive,
  } = props;

  const { classes } = useStyles();
  const formik = useFormikContext();
  const { inClaimPage } = useClaim();
  const communicationClaimId = communication?.claim_id;
  const [claim] = useFetchClaim(communicationClaimId);

  // Use Clive config directly without relying on CliveClaimContext
  const { isCliveEnabled, isCliveEnabledForOrganization } = useCliveConfigOptions();

  const [requestedAsFullscreen, setRequestedAsFullscreen] = useState(false);
  const [communicationIdsUpdating, setCommunicationIdsUpdating] = useState([]);

  const { communicationCategories, communicationConfiguration } = useOrganization();
  const showCommunicationCategories = displayCommunicationCategories(communicationCategories);
  const showHandledStatus = communicationConfiguration?.is_handled_status_enabled === true;
  const configCategoryRequired = communicationConfiguration?.is_category_required === true;

  const { isSubmitting } = formik;
  const isView = !isNew && !isDocument;

  let channel;
  let direction;
  let contact;

  if (isView) {
    channel = communication.channel;
    contact = communication.contact;
    direction = communication.direction;
  } else {
    const { communicationChannel, communicationContact, communicationDirection } = communicationInitialFields;
    channel = communicationChannel;
    contact = communicationContact;
    direction = communicationDirection;
  }

  const effectiveDirection = direction || formik?.values?.direction;
  const isCategoryRequired = effectiveDirection === 'Outgoing' && configCategoryRequired;

  const closeHandler = disableSoftClose || isEditing ? undefined : onClose;
  let dialogProps = {};
  if (isDialog) {
    dialogProps = {
      isDialog,
      onClose: closeHandler,
      maxWidth: maxWidth ? maxWidth : 'sm',
      fullWidth: true,
    };
  }
  const fullScreenProps = {
    isDialog: true,
    onClose: () => setRequestedAsFullscreen(false),
    maxWidth: 'lg',
    fullWidth: true,
  };

  const displayDetachAction =
    isView &&
    !isEditing &&
    communicationClaimId &&
    ['email', 'phone_call', 'physical_mail', 'sms'].includes(communication.channel);
  const displayNotificationAction = isView && !isEditing && !!communication.claim_id;

  const rightCardActions = React.Children.map(additionalCardActions, (child) =>
    React.cloneElement(child, {
      displayDetachAction,
      displayNotificationAction,
    })
  );

  const action = (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      {leftCardActions}
      {!requestedAsFullscreen && !isDialog && (
        <RestrictedPermissions verb={PERMISSION_VERBS.READ}>
          <span>
            <FsIconButton tooltipText="Full screen" icon={ExpandIcon} onClick={() => setRequestedAsFullscreen(true)} />
          </span>
        </RestrictedPermissions>
      )}
      {rightCardActions}
      {closeHandler && !isDialog && (
        <FsIconButton tooltipText="Close" icon={CloseIcon} onClick={closeHandler} ignorePermissions />
      )}
    </div>
  );

  const clivePanel = () => {
    const hasClaim = !!claim;
    let isCliveActive;

    if (hasClaim) {
      const subOrganizationId = claim.sub_organization_id ?? claim.policy?.sub_organization_id;
      isCliveActive = isCliveEnabled(subOrganizationId);
    } else {
      isCliveActive = isCliveEnabledForOrganization();
    }

    if (!isCliveActive) {
      return null;
    }

    const subTitle = hasClaim
      ? "Once the recording ends, I'll analyze the call to generate a summary, highlight key outcomes, and provide the full transcript. This may take a few minutes."
      : "Once the call is attached to a claim, I'll analyze the call to generate a summary, highlight key outcomes, and provide the full transcript. This may take a few minutes";

    return <CliveRecommendationPanel title="Clive's Overview" subtitle={subTitle} />;
  };

  const additionalHeaderProp = communicationHeaderAdditionalProps ? communicationHeaderAdditionalProps : {};

  const handleSetIsUpdating = (isUpdating) => {
    if (isUpdating) {
      setCommunicationIdsUpdating((prev) => [...prev, communication.id]);
    } else {
      setCommunicationIdsUpdating((prev) => prev.filter((id) => id !== communication.id));
    }
  };

  const handleChipUpdate = async () => {
    await onRefreshViewPanelData?.();
    await onRefreshCurrentPage?.();
  };

  const categoryChips =
    showCommunicationCategories && (isNew || isDocument) ? (
      <div>
        <CommunicationCategorySelectChip
          value={formik?.values?.category_id ?? null}
          onChange={(categoryId) => formik?.setFieldValue?.('category_id', categoryId)}
          disabled={isSubmitting}
          required={isCategoryRequired}
        />
        <ErrorHelperTextFormik id="category_id" />
      </div>
    ) : communication?.id && isView ? (
      <>
        {showHandledStatus && direction === 'Incoming' && (
          <CommunicationHandledStatusChip
            communicationId={communication.id}
            isHandled={communication.is_handled === true}
            disabled={communicationIdsUpdating.includes(communication.id)}
            setIsUpdating={handleSetIsUpdating}
            onUpdate={handleChipUpdate}
          />
        )}
        {showCommunicationCategories && (
          <CommunicationCategoryChip
            communication={communication}
            disabled={communicationIdsUpdating.includes(communication.id)}
            setIsUpdating={handleSetIsUpdating}
            onUpdate={handleChipUpdate}
          />
        )}
      </>
    ) : null;

  return (
    <>
      {/* This is our way of showing the communication in 'Full Screen' - open in it again but as large dialog  */}
      {requestedAsFullscreen && <CommunicationCard {...props} {...fullScreenProps} />}
      <CardDialog
        action={action}
        open={open}
        {...dialogProps}
        title={title ? title : undefined}
        preventClose={isSubmitting}
        trackAlt="Communication"
        onMinimized={onMinimized}
        disableMinimized={disableMinimized}
        subheader={
          <div className="flex flex-col gap-20">
            {shouldShowClive && <div className="mr-24">{clivePanel()}</div>}
            {(!inClaimPage || isCommunicationNotRelatedToClaimInContext) &&
              claim && ( // Show link to the claim only if communication's claim different from the claim page were in, or we're outside claim page
                <ClaimLink claimId={claim.id} linkText={`Claim: ${claim.claim_id_display}`} />
              )}
          </div>
        }
        containerStyle={cardDialogContainerStyle}
        containerClassName={cardDialogContainerClassName}
      >
        {AdditionalDialogHeader}
        {senderFragment}
        <CommunicationFullHeader
          communication={communication ? communication : { channel, direction, contact }}
          communicationClaimId={communicationClaimId}
          channel={channel}
          direction={direction}
          contact={contact}
          isView={isView}
          onCommunicationUpdate={onCommunicationUpdate}
          additionalHeaderProp={additionalHeaderProp}
          communicationIdentifier={communicationIdentifier}
          newPhoneCommunicationUI={newPhoneCommunicationUI}
          isNew={isNew}
          isDocument={isDocument}
          categoryChips={categoryChips}
        />
        <CliveAnalysisSection communication={communication} showClive={showClive} />
        {BannerBelowHeader && isView ? BannerBelowHeader : null}
        <CommunicationTypeSpecificBody
          communication={communication}
          communicationInitialFields={communicationInitialFields}
          isDocument={isDocument}
          isEditing={isEditing}
          onRequestEdit={onRequestEdit}
          isNew={isNew}
          {...communicationTypeSpecificBodyProps}
          showTranslation={showTranslation}
        />

        {(!isView || communication.summary || isEditing || onRequestEdit) && ( // show summary field only if exists or can be edited
          <div className={classes.inputContainer}>
            <TextFieldFormik
              id="summary"
              label="Summary"
              className={classes.textField}
              fullWidth
              onEdit={onRequestEdit}
              showOnly={isView && !isEditing}
            />
          </div>
        )}

        {(!isView || communication.claim_id) && ( // don't display if we're in an existing communication which is not yet claim related
          <CommunicationExposureLabels isView={isView} isEditing={isEditing} onEdit={onRequestEdit} />
        )}

        <CommunicationAction
          communication={communication}
          isDocument={isDocument}
          isEditing={isEditing}
          isNew={isNew}
          {...communicationActionProps}
        />
      </CardDialog>
    </>
  );
};

CommunicationCard.propTypes = {
  open: PropTypes.bool,
  communication: requiredIf(PropTypes.object, (props) => !props.isNew && !props.isDocument),
  onCommunicationUpdate: PropTypes.func,
  onRefreshViewPanelData: PropTypes.func,
  onRefreshCurrentPage: PropTypes.func,
  communicationIdentifier: PropTypes.string,
  isDialog: PropTypes.bool,
  title: PropTypes.string || PropTypes.node,
  disableSoftClose: PropTypes.bool,
  onClose: requiredIf(PropTypes.func, (props) => props.isDialog && !props.disableSoftClose),
  onMinimized: PropTypes.func,
  isNew: PropTypes.bool,
  isDocument: PropTypes.bool,
  disableMinimized: PropTypes.bool,
  additionalCardActions: PropTypes.node,
  communicationInitialFields: PropTypes.shape({
    communicationChannel: PropTypes.string.isRequired,
    communicationContact: PropTypes.object,
    communicationDirection: requiredIf(PropTypes.oneOf(['Incoming', 'Outgoing']), (props) => props.isNew),
  }),
  communicationHeaderAdditionalProps: PropTypes.object,
  CommunicationTypeSpecificBody: PropTypes.func.isRequired,
  communicationTypeSpecificBodyProps: PropTypes.object,
  CommunicationAction: PropTypes.func.isRequired,
  communicationActionProps: PropTypes.object,
  isEditing: PropTypes.bool,
  showTranslation: PropTypes.bool,
  onRequestEdit: PropTypes.func,
  formik: PropTypes.object, // From formik
  maxWidth: PropTypes.string,
  isCommunicationNotRelatedToClaimInContext: PropTypes.bool,
  senderFragment: PropTypes.node,
  leftCardActions: PropTypes.node,
  cardDialogContainerStyle: PropTypes.object,
  cardDialogContainerClassName: PropTypes.string,
  AdditionalDialogHeader: PropTypes.node,
  BannerBelowHeader: PropTypes.node,
  newPhoneCommunicationUI: PropTypes.bool, // TODO NGTPA-20266: remove this prop when new phone communication UI is fully implemented
  showClive: PropTypes.bool,
  shouldShowClive: PropTypes.bool,
};

export default CommunicationCard;
